// Booking schedule to send to a job's accommodation provider.
// Laid out to match TPA's existing sheet: Room / First Name / Last Name / Arrival /
// Departure / Confirmation No. / Notes, with changes since the last send highlighted.
import React, { useEffect, useState } from 'react';
import { api, download, currentUser } from '../api';
import { Spinner, ErrorBox, DateField, Confirm } from '../ui';
import { todayDarwin, addMonths, toAus } from '../util';

const YELLOW = '#ffff00';

export default function Schedule() {
  const [projects, setProjects] = useState<any[]>([]);
  const [projectId, setProjectId] = useState<number | ''>('');
  const [from, setFrom] = useState(todayDarwin());
  const [to, setTo] = useState(addMonths(todayDarwin(), 6));
  const [data, setData] = useState<any | null>(null);
  const [error, setError] = useState<string[] | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [confirmSend, setConfirmSend] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState<number | null>(null);

  // Ticks (or un-ticks) one check-in/check-out cycle as booked with the provider.
  const confirmRow = async (row: any, booked: boolean, ref: string) => {
    setSaving(row.attendance_period_id);
    setError(null);
    try {
      await api('/schedule/confirm', {
        method: 'POST',
        body: { attendance_period_id: row.attendance_period_id, booked, booking_ref: ref }
      });
      load();
    } catch (e: any) {
      setError(e.errors?.length ? e.errors : [e.message]);
    } finally { setSaving(null); }
  };

  useEffect(() => {
    api('/projects').then(p => {
      setProjects(p);
      if (p.length && projectId === '') setProjectId(p[0].id);
    }).catch(e => setError([e.message]));
  }, []);

  const load = () => {
    if (projectId === '') return;
    setData(null);
    setError(null);
    api(`/schedule?project_id=${projectId}&from=${from}&to=${to}`)
      .then(setData).catch(e => setError([e.message]));
  };
  useEffect(load, [projectId, from, to]);

  // Build an HTML table so pasting into Excel keeps the yellow highlighting.
  const copyForExcel = async () => {
    if (!data) return;
    const cell = (v: string, hi: boolean, bold = false) =>
      `<td style="border:1px solid #000;${hi ? `background:${YELLOW};` : ''}${bold ? 'font-weight:bold;' : ''}">${escapeHtml(v)}</td>`;
    const head = ['', 'First Name', 'Last Name', 'Arrival Date', 'Departure Date', 'Confirmation No.', 'Notes']
      .map(h => `<th style="border:1px solid #000;background:${YELLOW};font-weight:bold;">${escapeHtml(h)}</th>`).join('');
    const body = data.rows.map((r: any) => {
      const revised = r.row_state === 'revised' || r.row_state === 'new' || r.row_state === 'cancelled';
      return '<tr>' +
        cell(r.room_label, false, true) +
        cell(r.first_name, false) +
        cell(r.last_name, false) +
        cell(r.arrival_display, r.changed.arrival) +
        cell(r.departure_display, r.changed.departure) +
        cell(r.confirmation_no, r.changed.confirmation) +
        cell(r.note, revised && !!r.note) +
        '</tr>';
    }).join('');
    const html = `<table style="border-collapse:collapse">${`<tr>${head}</tr>`}${body}</table>`;
    const text = [
      ['', 'First Name', 'Last Name', 'Arrival Date', 'Departure Date', 'Confirmation No.', 'Notes'].join('\t'),
      ...data.rows.map((r: any) => [r.room_label, r.first_name, r.last_name,
        r.arrival_display, r.departure_display, r.confirmation_no, r.note].join('\t'))
    ].join('\n');
    try {
      await navigator.clipboard.write([new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' })
      })]);
      setMsg('Copied. Paste straight into your Excel sheet — the yellow highlighting comes across.');
    } catch {
      try {
        await navigator.clipboard.writeText(text);
        setMsg('Copied as plain text (your browser blocked formatted copy, so highlighting is not included).');
      } catch { setError(['Could not access the clipboard. Use Download CSV instead.']); }
    }
  };

  const markSent = async () => {
    setBusy(true);
    setConfirmSend(false);
    try {
      await api('/schedule/mark-sent', { method: 'POST', body: { project_id: projectId, from, to } });
      setMsg('Recorded as sent. From now on only newer changes will be highlighted.');
      load();
    } catch (e: any) { setError(e.errors?.length ? e.errors : [e.message]); }
    finally { setBusy(false); }
  };

  const readonly = currentUser?.role === 'readonly' || currentUser?.role === 'supervisor';

  return (
    <div className="page">
      <div className="page-head no-print">
        <h2>Booking schedule</h2>
        <div className="btn-row">
          <select className="input" value={projectId} onChange={e => setProjectId(Number(e.target.value))}>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <span className="muted">From</span><div style={{ width: 150 }}><DateField value={from} onChange={v => v && setFrom(v)} /></div>
          <span className="muted">to</span><div style={{ width: 150 }}><DateField value={to} onChange={v => v && setTo(v)} /></div>
        </div>
      </div>

      <ErrorBox error={error} onDismiss={() => setError(null)} />
      {msg && (
        <div className="panel no-print" style={{ borderLeft: '4px solid var(--olive)' }}>
          {msg} <button className="btn btn-ghost btn-sm" onClick={() => setMsg(null)}>Dismiss</button>
        </div>
      )}

      {!data ? <Spinner label="Building schedule…" /> : (
        <>
          {/* Cover details — each job has its own provider and location */}
          <div className="panel">
            <div className="two-col">
              <div>
                <h3 style={{ marginBottom: 4 }}>{data.project.name}</h3>
                <div style={{ fontSize: 13 }}>
                  {data.project.code && <>Project code: {data.project.code}<br /></>}
                  Location: <strong>{data.project.location || 'not set'}</strong><br />
                  Period: {toAus(data.from)} to {toAus(data.to)}
                </div>
              </div>
              <div style={{ fontSize: 13 }}>
                <strong>Bookings sent to</strong><br />
                {data.provider ? (
                  <>
                    {data.provider.name}<br />
                    {data.provider.contact_person && <>{data.provider.contact_person}<br /></>}
                    {data.provider.phone && <>{data.provider.phone}<br /></>}
                    {data.provider.email && <>{data.provider.email}</>}
                  </>
                ) : (
                  <span style={{ color: 'var(--danger)' }}>
                    No accommodation provider set for this job. Set one in Settings → Projects
                    (“Default accommodation provider”).
                  </span>
                )}
              </div>
            </div>
            <div className="btn-row no-print" style={{ marginTop: 12 }}>
              <button className="btn btn-primary" onClick={copyForExcel}>Copy for Excel</button>
              <button className="btn" onClick={() => download(`/schedule/csv?project_id=${projectId}&from=${from}&to=${to}`)}>Download CSV</button>
              <button className="btn" onClick={() => window.print()}>Print / PDF</button>
              {!readonly && (
                <button className="btn" disabled={busy} onClick={() => setConfirmSend(true)}>
                  Mark as sent
                </button>
              )}
              <span className="muted">
                {data.summary.new} new · {data.summary.revised} revised · {data.summary.cancelled} to cancel · {data.summary.total} rows
                {data.last_sent_at ? ` · last sent ${data.last_sent_at.replace('T', ' ').slice(0, 16)}` : ' · never sent'}
              </span>
            </div>
            <div className="btn-row" style={{ marginTop: 8 }}>
              <span className="chip" style={{ background: data.summary.awaiting_booking ? '#c9a227' : 'var(--olive)' }}>
                {data.summary.booked} of {data.summary.booked + data.summary.awaiting_booking} cycles booked
              </span>
              {data.can_confirm
                ? <span className="muted">Tick the <strong>Booked</strong> box once you have reserved the rooms, and put the provider's reference in the Confirmation No. column.</span>
                : <span className="muted">The Booked column is set by whoever reserves the rooms.</span>}
            </div>
          </div>

          <div className="table-wrap">
            <div className="table-scroll">
              <table className="sched-table">
                <thead>
                  <tr>
                    <th></th><th>First Name</th><th>Last Name</th>
                    <th>Arrival Date</th><th>Departure Date</th>
                    <th>Confirmation No.</th><th>Booked</th><th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.length === 0 && (
                    <tr><td colSpan={8} className="muted" style={{ padding: 20 }}>
                      Nobody needs accommodation in this period for this job.
                    </td></tr>
                  )}
                  {data.rows.map((r: any) => {
                    const revised = r.row_state !== 'unchanged';
                    return (
                      <tr key={r.attendance_period_id}
                        className={r.row_state === 'cancelled' ? 'sched-cancelled' : ''}>
                        <td style={{ fontWeight: 700 }}>{r.room_label}</td>
                        <td>{r.first_name}</td>
                        <td>{r.last_name}</td>
                        <td className={r.changed.arrival ? 'hl' : ''}>{r.arrival_display}</td>
                        <td className={r.changed.departure ? 'hl' : ''}>{r.departure_display}</td>
                        <td className={r.changed.confirmation ? 'hl' : ''}>
                          {data.can_confirm && r.row_state !== 'cancelled' ? (
                            <input className="input" style={{ minWidth: 110 }}
                              defaultValue={r.confirmation_no}
                              placeholder="reference"
                              onBlur={e => {
                                if (e.target.value === r.confirmation_no) return;
                                confirmRow(r, r.booked, e.target.value);
                              }} />
                          ) : r.confirmation_no}
                        </td>
                        <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}
                          title={r.booked_at ? `Confirmed by ${r.booked_by} on ${r.booked_at.replace('T', ' ').slice(0, 16)}` : 'Not yet booked'}>
                          {data.can_confirm && r.row_state !== 'cancelled' ? (
                            <input type="checkbox" className="checkbox" checked={!!r.booked}
                              disabled={saving === r.attendance_period_id}
                              onChange={e => confirmRow(r, e.target.checked, r.confirmation_no)} />
                          ) : (r.booked ? '✓' : '')}
                          {r.booked && r.booked_by && (
                            <div className="muted" style={{ fontSize: 10 }}>{r.booked_by.split(' ')[0]}</div>
                          )}
                        </td>
                        <td className={revised && r.note ? 'hl' : ''} style={{ fontWeight: revised ? 700 : 400 }}>
                          {r.note}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <p className="muted no-print" style={{ marginTop: 8 }}>
            Yellow cells are what changed since you last marked this schedule as sent. Revision
            wording is generated automatically. Room numbers and confirmation numbers appear once
            you record them against the booking (Accommodation → Bookings → assign personnel).
            Date format and the room label prefix are per-job settings under Settings → Projects.
          </p>
        </>
      )}

      {confirmSend && (
        <Confirm
          message={`Mark this schedule as sent to ${data?.provider?.name || 'the provider'}?\n\nThis records the current dates as the agreed position. Next time, only changes made after now will be highlighted. Do this once you have actually emailed the sheet.`}
          onYes={markSent} onNo={() => setConfirmSend(false)} />
      )}
    </div>
  );
}

function escapeHtml(s: string) {
  return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
